import {
  ChartMark,
  WidgetKind,
  type Report,
  type ReportGroup,
} from 'corso-admin-ui';

// Superset's "web channels": orders placed through a storefront
const online = { isOnline: true };

const attachRate = {
  key: 'attach-rate',
  label: 'Attach rate',
  dataset: 'orders',
  timeFields: ['orderedOn'],
  widgets: [
    {
      kind: WidgetKind.kpi,
      title: 'Attach rate (web channels)',
      description: 'Online orders that are protected',
      metric: 'attachRate',
      filters: online,
      trend: true,
    },
    {
      kind: WidgetKind.kpi,
      title: 'Attach rate (all channels)',
      description: 'All orders that are protected',
      metric: 'attachRate',
      trend: true,
    },
    {
      kind: WidgetKind.kpi,
      title: 'Online orders',
      metric: 'orders',
      filters: online,
      trend: true,
    },
    {
      kind: WidgetKind.overTime,
      title: 'Attach rate over time (web channels)',
      description: 'Online orders that are protected',
      mark: ChartMark.line,
      metrics: ['attachRate'],
      filters: online,
    },
  ],
} as const satisfies Report;

const protectedOrders = {
  key: 'protected-orders',
  label: 'Protected orders',
  dataset: 'orders',
  timeFields: ['orderedOn'],
  widgets: [
    { kind: WidgetKind.kpi, metric: 'protectedOrders', trend: true },
    {
      kind: WidgetKind.kpi,
      description: "At the current price of each order's protection tier",
      metric: 'avgProtectionPrice',
      trend: true,
    },
    {
      kind: WidgetKind.kpi,
      description: "At the current price of each order's protection tier",
      metric: 'protectionPrice',
      trend: true,
    },
    {
      kind: WidgetKind.overTime,
      title: 'Protected orders over time',
      mark: ChartMark.line,
      metrics: ['protectedOrders'],
    },
  ],
} as const satisfies Report;

const salesChannels = {
  key: 'sales-channels',
  label: 'Sales channels',
  dataset: 'orders',
  timeFields: ['orderedOn'],
  widgets: [
    {
      kind: WidgetKind.overTime,
      title: 'Orders and protected orders',
      mark: ChartMark.line,
      metrics: ['orders', 'protectedOrders'],
    },
    {
      kind: WidgetKind.overTime,
      title: 'Attach rate over time (all channels)',
      mark: ChartMark.line,
      metrics: ['attachRate'],
    },
    {
      kind: WidgetKind.table,
      title: 'Attach rate by sales channel',
      description: 'Shopify names third-party channels by number',
      dimensions: ['salesChannel'],
      metrics: ['orders', 'protectedOrders', 'attachRate'],
      limit: 50,
    },
  ],
} as const satisfies Report;

const shippingClaimsOverview = {
  key: 'overview',
  label: 'Overview',
  dataset: 'shippingClaims',
  timeFields: ['createdOn', 'resolvedOn'],
  widgets: [
    { kind: WidgetKind.kpi, metric: 'claims', trend: true },
    {
      kind: WidgetKind.overTime,
      title: 'Claims over time',
      mark: ChartMark.line,
      metrics: ['claims'],
    },
  ],
} as const satisfies Report;

const warrantiesOverview = {
  key: 'overview',
  label: 'Overview',
  dataset: 'claims',
  timeFields: ['createdOn', 'finalizedOn'],
  widgets: [
    { kind: WidgetKind.kpi, metric: 'claims', trend: true },
    {
      kind: WidgetKind.overTime,
      title: 'Requests over time',
      mark: ChartMark.line,
      metrics: ['claims'],
    },
  ],
} as const satisfies Report;

const registrationsOverview = {
  key: 'overview',
  label: 'Overview',
  dataset: 'registrations',
  timeFields: ['createdOn', 'purchasedOn'],
  widgets: [
    { kind: WidgetKind.kpi, metric: 'registrations', trend: true },
    {
      kind: WidgetKind.overTime,
      title: 'Registrations over time',
      mark: ChartMark.line,
      metrics: ['registrations'],
    },
  ],
} as const satisfies Report;

const byResolvedDay = 'By the day the items were resolved';
const unresolvedLeftOut = 'Items with no resolution yet are left out.';

const returnsOverview = {
  key: 'overview',
  label: 'Overview',
  dataset: 'claimItems',
  timeFields: ['createdOn'],
  widgets: [
    {
      kind: WidgetKind.kpi,
      title: 'Return requests',
      metric: 'claims',
      trend: true,
    },
    {
      kind: WidgetKind.kpi,
      title: 'Items returned',
      metric: 'items',
      trend: true,
    },
    {
      kind: WidgetKind.kpi,
      description: 'Of items ordered in this period, returned so far',
      metric: 'returnRate',
      dataset: 'orderItems',
      timeField: 'createdOn',
      trend: true,
    },
    { kind: WidgetKind.kpi, metric: 'itemsPerClaim', trend: true },
    {
      kind: WidgetKind.kpi,
      description: byResolvedDay,
      metric: 'refundDeflectionRate',
      timeField: 'finalizedOn',
      trend: true,
    },
    {
      kind: WidgetKind.kpi,
      description: byResolvedDay,
      metric: 'refundSaveRate',
      timeField: 'finalizedOn',
      trend: true,
    },
    {
      kind: WidgetKind.overTime,
      title: 'Returns over time',
      description: 'Return requests, and the items on them',
      mark: ChartMark.line,
      metrics: ['claims', 'items'],
    },
    {
      kind: WidgetKind.overTime,
      title: 'Refunds prevented over time',
      description: byResolvedDay,
      mark: ChartMark.line,
      metrics: ['refundDeflectionRate', 'refundSaveRate'],
      timeField: 'finalizedOn',
    },
  ],
} as const satisfies Report;

const returnResolutions = {
  key: 'resolutions',
  label: 'Resolutions',
  dataset: 'claimItems',
  timeFields: ['finalizedOn'],
  widgets: [
    { kind: WidgetKind.kpi, metric: 'refundDeflectionRate', trend: true },
    { kind: WidgetKind.kpi, metric: 'refundSaveRate', trend: true },
    {
      kind: WidgetKind.kpi,
      title: 'Line items resolved outside Corso',
      metric: 'resolvedExternally',
      trend: true,
    },
    {
      kind: WidgetKind.table,
      title: 'Resolution mix',
      description: 'Returned items by how they were resolved',
      dimensions: ['resolution'],
      metrics: ['items'],
      share: 'items',
    },
    {
      kind: WidgetKind.flow,
      title: 'Requested to resolved',
      info: unresolvedLeftOut,
      dimensions: ['requestedResolution', 'resolution'],
      metric: 'items',
      otherLabel: 'Other resolutions',
      hideUnset: true,
    },
    {
      kind: WidgetKind.table,
      title: 'Altered resolutions',
      description: 'Items resolved another way than requested',
      dimensions: ['requestedResolution', 'resolution'],
      metrics: ['items'],
      filters: { wasResolutionChanged: true },
    },
  ],
} as const satisfies Report;

const returnReasons = {
  key: 'reasons',
  label: 'Reasons',
  dataset: 'claimItems',
  timeFields: ['createdOn', 'finalizedOn'],
  widgets: [
    {
      kind: WidgetKind.table,
      title: 'Reason categories',
      description:
        'How often each comes up, and how often it is kept from a refund',
      dimensions: ['reasonCategory'],
      metrics: ['items', 'refundDeflectionRate', 'refundSaveRate'],
      share: 'items',
      // every category, and the unset one
      limit: 10,
    },
    {
      kind: WidgetKind.flow,
      title: 'Reason category to resolution',
      info: unresolvedLeftOut,
      dimensions: ['reasonCategory', 'resolution'],
      metric: 'items',
      otherLabel: 'Other categories',
      hideUnset: true,
    },
  ],
} as const satisfies Report;

const returnSpeed = {
  key: 'speed',
  label: 'Speed',
  dataset: 'claimItems',
  timeFields: ['finalizedOn'],
  widgets: [
    { kind: WidgetKind.kpi, metric: 'avgDaysClaimToResolved', trend: true },
    { kind: WidgetKind.kpi, metric: 'avgDaysClaimToTransit', trend: true },
    { kind: WidgetKind.kpi, metric: 'avgDaysInTransit', trend: true },
    { kind: WidgetKind.kpi, metric: 'avgDaysDeliveredToResolved', trend: true },
    {
      kind: WidgetKind.overTime,
      title: 'Time from claim to resolved',
      mark: ChartMark.line,
      metrics: ['avgDaysClaimToResolved'],
    },
  ],
} as const satisfies Report;

const conciergeOverview = {
  key: 'overview',
  label: 'Overview',
  dataset: 'supportConversations',
  timeFields: ['startedOn'],
  widgets: [
    { kind: WidgetKind.kpi, metric: 'conversations', trend: true },
    {
      kind: WidgetKind.kpi,
      description: 'By the day of the first response',
      metric: 'avgFirstResponseMinutes',
      timeField: 'firstRespondedOn',
      trend: true,
    },
    {
      kind: WidgetKind.kpi,
      metric: 'messagesSent',
      dataset: 'supportMessages',
      timeField: 'sentOn',
      trend: true,
    },
    {
      kind: WidgetKind.kpi,
      metric: 'messagesReceived',
      dataset: 'supportMessages',
      timeField: 'sentOn',
      trend: true,
    },
    {
      kind: WidgetKind.kpi,
      metric: 'csatScore',
      dataset: 'supportCsat',
      timeField: 'ratedOn',
      trend: true,
    },
    {
      kind: WidgetKind.kpi,
      metric: 'scoresCollected',
      dataset: 'supportCsat',
      timeField: 'ratedOn',
      trend: true,
    },
    {
      kind: WidgetKind.overTime,
      title: 'New conversations over time',
      mark: ChartMark.line,
      metrics: ['conversations'],
    },
    {
      kind: WidgetKind.overTime,
      title: 'Messages over time',
      mark: ChartMark.line,
      metrics: ['messagesSent', 'messagesReceived'],
      dataset: 'supportMessages',
      timeField: 'sentOn',
    },
    {
      kind: WidgetKind.overTime,
      title: 'CSAT score over time',
      mark: ChartMark.line,
      metrics: ['csatScore'],
      dataset: 'supportCsat',
      timeField: 'ratedOn',
    },
    {
      kind: WidgetKind.table,
      title: 'Messages by sender',
      dimensions: ['sentBy'],
      metrics: ['messagesSent', 'messagesReceived'],
      dataset: 'supportMessages',
      timeField: 'sentOn',
    },
  ],
} as const satisfies Report;

/** The platform dashboards, in the order the sidebar lists them. */
export const platformReportGroups = [
  {
    key: 'shipping-protection',
    label: 'Shipping protection',
    filters: {},
    reports: [attachRate, protectedOrders, salesChannels],
  },
  {
    key: 'shipping-claims',
    label: 'Shipping claims',
    filters: {},
    reports: [shippingClaimsOverview],
  },
  {
    key: 'returns',
    label: 'Returns',
    filters: {
      claimItems: { claimTypes: ['Return'] },
      claims: { claimTypes: ['Return'] },
    },
    reports: [returnsOverview, returnResolutions, returnReasons, returnSpeed],
  },
  {
    key: 'warranties',
    label: 'Warranties',
    filters: { claims: { claimTypes: ['Warranty'] } },
    reports: [warrantiesOverview],
  },
  {
    key: 'registrations',
    label: 'Registrations',
    filters: {},
    reports: [registrationsOverview],
  },
  {
    key: 'concierge',
    label: 'Concierge',
    filters: {},
    reports: [conciergeOverview],
  },
] as const satisfies ReportGroup[];
